import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { useAuthentication } from "./composables/useAuthentication";

const routes: RouteRecordRaw[] = [
  {
    component: () => import("./pages/LoginPage.vue"),
    name: "login",
    path: "/login",
  },
  {
    children: [
      {
        component: () => import("./pages/DashboardPage.vue"),
        name: "dashboard",
        path: "",
      },
      {
        component: () => import("./pages/AppointmentDetailPage.vue"),
        name: "appointment-detail",
        path: "termine/:id",
      },
      {
        component: () => import("./pages/AppointmentEditPage.vue"),
        name: "appointment-edit",
        path: "termine/:id/bearbeiten",
      },
      {
        component: () => import("./pages/ServiceCategoriesPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "service-categories",
        path: "dienstleistungen/kategorien",
      },
      {
        component: () => import("./pages/ServiceCategoryImportPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "service-category-import",
        path: "dienstleistungen/kategorien/import",
      },
      {
        component: () => import("./pages/ServiceCategoryEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "service-category-edit",
        path: "dienstleistungen/kategorien/:id",
      },
      {
        component: () => import("./pages/ServicesPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "services",
        path: "dienstleistungen",
      },
      {
        component: () => import("./pages/ServiceImportPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "service-import",
        path: "dienstleistungen/import",
      },
      {
        component: () => import("./pages/ServiceEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "service-edit",
        path: "dienstleistungen/:id",
      },
      {
        component: () => import("./pages/ProductCategoriesPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "product-categories",
        path: "produkte/kategorien",
      },
      {
        component: () => import("./pages/ProductCategoryImportPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "product-category-import",
        path: "produkte/kategorien/import",
      },
      {
        component: () => import("./pages/ProductCategoryEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "product-category-edit",
        path: "produkte/kategorien/:id",
      },
      {
        component: () => import("./pages/ProductsPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "products",
        path: "produkte",
      },
      {
        component: () => import("./pages/ProductImportPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "product-import",
        path: "produkte/import",
      },
      {
        component: () => import("./pages/ProductEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "product-edit",
        path: "produkte/:id",
      },
      {
        component: () => import("./pages/PickupDetailPage.vue"),
        name: "pickup-detail",
        path: "abholungen/:id",
      },
      {
        component: () => import("./pages/CustomersPage.vue"),
        name: "customers",
        path: "kunden",
      },
      {
        component: () => import("./pages/CustomerCreatePage.vue"),
        name: "customer-create",
        path: "kunden/neu",
      },
      {
        component: () => import("./pages/CustomerDetailPage.vue"),
        name: "customer-detail",
        path: "kunden/:id",
      },
      {
        component: () => import("./pages/CustomerEditPage.vue"),
        name: "customer-edit",
        path: "kunden/:id/bearbeiten",
      },
      {
        component: () => import("./pages/StaffPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "staff",
        path: "mitarbeiter",
      },
      {
        component: () => import("./pages/StaffEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "staff-edit",
        path: "mitarbeiter/:id",
      },
      {
        component: () => import("./pages/StaffTimeEntriesPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "staff-time-entries",
        path: "arbeitszeit",
      },
      {
        component: () => import("./pages/EmailTemplatesPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "email-templates",
        path: "email-vorlagen",
      },
      {
        component: () => import("./pages/EmailTemplateEditPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "email-template-edit",
        path: "email-vorlagen/:id",
      },
      {
        component: () => import("./pages/BusinessHoursPage.vue"),
        meta: {
          requiresPermission: "editCatalog",
        },
        name: "business-hours",
        path: "oeffnungszeiten",
      },
      {
        component: () => import("./pages/UsersPage.vue"),
        meta: {
          requiresPermission: "manageUsers",
        },
        name: "users",
        path: "benutzer",
      },
      {
        component: () => import("./pages/UserEditPage.vue"),
        meta: {
          requiresPermission: "manageUsers",
        },
        name: "user-edit",
        path: "benutzer/:id",
      },
      {
        component: () => import("./pages/SettingsPage.vue"),
        meta: {
          requiresPermission: "manageSettings",
        },
        name: "settings",
        path: "einstellungen",
      },
      {
        component: () => import("./pages/ActivityLogPage.vue"),
        meta: {
          requiresPermission: "manageSettings",
        },
        name: "activity-log",
        path: "aktivitaeten",
      },
      {
        component: () => import("./pages/NotificationsPage.vue"),
        meta: {
          requiresPermission: "manageSettings",
        },
        name: "notifications",
        path: "benachrichtigungen",
      },
      {
        component: () => import("./pages/ClientErrorsPage.vue"),
        meta: {
          requiresPermission: "manageSettings",
        },
        name: "client-errors",
        path: "fehler",
      },
    ],
    component: () => import("./components/Layout.vue"),
    meta: {
      requiresAuthentication: true,
    },
    path: "/",
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return {
      behavior: "smooth",
      left: 0,
      top: 0,
    };
  },
});

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuthentication) {
    return true;
  }

  const authentication = useAuthentication();

  if (!authentication.hasResolvedSession.value) {
    await authentication.loadCurrentUser().catch(() => null);
  }

  if (!authentication.isAuthenticated.value) {
    return {
      name: "login",
      query: {
        redirect: to.fullPath,
      },
    };
  }

  const requiredPermission = to.meta.requiresPermission as "editCatalog" | "manageSettings" | "manageUsers" | undefined;
  if (requiredPermission && !authentication.can(requiredPermission)) {
    return {
      name: "dashboard",
    };
  }

  return true;
});
